import { supabase } from "./supabase";
import { isNetworkError } from "./network";

/**
 * Single source of truth for the access token, and for whether the app can
 * currently talk to the server.
 *
 * The bug this exists to kill: every call site used to do its own
 * `supabase.auth.getSession()` and take whatever came back — including an
 * expired token, which the server rejects. `autoRefreshToken` refreshes on a
 * timer, and browsers throttle or suspend timers in background tabs and across
 * laptop sleep, so waking up past the expiry left the app holding a dead token
 * with no path back. The UI kept rendering because `user` was still populated,
 * so the failure was completely silent: writing didn't save, research did
 * nothing, the dashboard showed no documents, and only a refresh fixed it.
 *
 * Everything that needs a token goes through `getAccessToken()` here.
 */

export type ConnectionState =
  /** Requests are succeeding. */
  | "connected"
  /** A token refresh is in flight, or a request failed and we're recovering. */
  | "reconnecting"
  /** The network is gone; the Mac app can keep working from its local library. */
  | "offline"
  /** Refresh failed for good — the user has to sign in again. */
  | "signed-out";

/** Refresh this far ahead of expiry so a request never races the deadline. */
const REFRESH_MARGIN_MS = 60_000;

let state: ConnectionState = "connected";
const listeners = new Set<(s: ConnectionState) => void>();

export function getConnectionState() {
  return state;
}

export function subscribeToConnection(fn: (s: ConnectionState) => void) {
  listeners.add(fn);
  // Returns void, not Set.delete's boolean — this is used directly as a
  // useEffect cleanup, which must not return a value.
  return () => {
    listeners.delete(fn);
  };
}

/**
 * Bumped whenever the app comes back from a bad connection.
 *
 * Recovering the token was never enough on its own: every list in the app
 * fetches once on mount and never again, so after an outage the session was
 * healthy but the screen stayed empty — which is exactly why a browser refresh
 * appeared to be the only fix. Components include this in their effect deps so
 * they re-fetch when connectivity returns.
 */
let reconnectEpoch = 0;
const reconnectListeners = new Set<(epoch: number) => void>();

export function getReconnectEpoch() {
  return reconnectEpoch;
}

export function subscribeToReconnect(fn: (epoch: number) => void) {
  reconnectListeners.add(fn);
  return () => {
    reconnectListeners.delete(fn);
  };
}

function bumpReconnect() {
  reconnectEpoch += 1;
  for (const fn of reconnectListeners) fn(reconnectEpoch);
}

function setConnectionState(next: ConnectionState) {
  if (state === next) return;
  const recovered = next === "connected" && state !== "connected";
  state = next;
  for (const fn of listeners) fn(next);
  // Only after listeners see "connected", so a refetch isn't issued while the
  // UI still believes it is offline.
  if (recovered) bumpReconnect();
}

/**
 * Marks the connection healthy. Called after any successful API response.
 *
 * A 2xx from an authenticated request is proof the session is alive, so this
 * is allowed to recover from "signed-out". That state used to stick on
 * purpose, but a false 401 (Suggest forwarding Anthropic's rejected key)
 * then painted the whole Mac app as logged out until a reload.
 */
export function reportSuccess() {
  setConnectionState("connected");
}

/** The hop failed but the session is still ours — keep working locally. */
export function reportOffline() {
  if (state !== "signed-out") setConnectionState("offline");
}

/** Marks the session unrecoverable — the UI should send the user to sign in. */
export function reportSignedOut() {
  setConnectionState("signed-out");
}

/** Epoch milliseconds at which this JWT expires, or null if unreadable. */
function expiryOf(token: string): number | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const exp = JSON.parse(json)?.exp;
    return typeof exp === "number" ? exp * 1000 : null;
  } catch {
    return null;
  }
}

function isUsable(token: string | null | undefined): token is string {
  if (!token) return false;
  const expiresAt = expiryOf(token);
  // An unreadable token is treated as usable — let the server be the judge
  // rather than locking the user out over a parsing quirk.
  if (expiresAt === null) return true;
  return expiresAt - Date.now() > REFRESH_MARGIN_MS;
}

/**
 * In-flight refresh, shared by every concurrent caller.
 *
 * Without this, a page that fires eight requests at once on an expired token
 * would trigger eight simultaneous refreshes. Supabase rotates refresh tokens,
 * so the losers of that race can invalidate the winner's token and sign the
 * user out — turning a recoverable blip into a forced logout.
 */
let refreshInFlight: Promise<string | null> | null = null;

async function refreshOnce(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  setConnectionState("reconnecting");
  refreshInFlight = (async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      const token = data.session?.access_token ?? null;
      if (error && isNetworkError(error)) {
        // A plane does not expire a login. Keep the session we have and let
        // the Mac library answer until the hop works again.
        setConnectionState("offline");
        return token;
      }
      if (error || !token) {
        // No session to recover. Don't call signOut() — that would clear
        // storage and race the auth listener; just report it and let
        // AuthContext drive the redirect.
        reportSignedOut();
        return null;
      }
      setConnectionState("connected");
      return token;
    } catch (error) {
      if (isNetworkError(error)) {
        setConnectionState("offline");
        const { data } = await supabase.auth.getSession();
        return data.session?.access_token ?? null;
      }
      setConnectionState("reconnecting");
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Current access token, refreshed if it is expired or about to be.
 *
 * Returns null when there is no recoverable session; callers should surface
 * that rather than sending `Bearer ` and reading the 401 as a server problem.
 */
export async function getAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token ?? null;

  if (isUsable(token)) return token;

  // Either absent, expired, or inside the refresh margin.
  return refreshOnce();
}

/** Force a refresh regardless of expiry — used once after an unexpected 401. */
export async function forceRefresh(): Promise<string | null> {
  return refreshOnce();
}

/**
 * Re-check the session when the tab wakes or the network returns.
 *
 * This is the case that actually bit: the laptop sleeps, the refresh timer
 * never fires, and the app wakes holding an expired token. Revalidating on
 * wake means recovery happens before the user's next keystroke fails to save,
 * rather than after.
 */
export function installSessionRecovery() {
  const revalidate = () => {
    if (window.document.visibilityState !== "visible") return;
    void getAccessToken();
  };

  const onVisibility = () => {
    if (window.document.visibilityState === "visible") {
      // Supabase schedules its refresh with setTimeout, and browsers throttle
      // or suspend timers in hidden tabs — so the scheduled refresh may never
      // have fired. Restarting it re-arms the timer against the real clock
      // instead of leaving it stranded.
      void supabase.auth.startAutoRefresh();
      revalidate();
    } else {
      void supabase.auth.stopAutoRefresh();
    }
  };

  const onOnline = () => {
    // The network came back. Re-validate, and if we were already healthy still
    // bump so views that failed while offline reload.
    void getAccessToken().then(() => {
      if (state === "connected") bumpReconnect();
    });
  };

  window.document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("online", onOnline);
  window.addEventListener("focus", revalidate);

  // The tab is visible right now, so make sure the timer is running.
  void supabase.auth.startAutoRefresh();

  return () => {
    window.document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("online", onOnline);
    window.removeEventListener("focus", revalidate);
  };
}
